import React from 'react';
import { Box, Card, CardContent, Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { Add, ArrowDownward, ArrowUpward, Delete, Edit } from '@mui/icons-material';
import { ConstructionQuestion, OPTIONS_COMPONENTS, QuestionType } from '@/types/questionnaire-construction';

interface QuestionCardProps {
  question: ConstructionQuestion;
  questionTypes: QuestionType[];
  readOnly: boolean;
  isFirst: boolean;
  isLast: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onAddOption: () => void;
  onEditOption: (optionId: number) => void;
  onDeleteOption: (optionId: number) => void;
}

export const QuestionCard: React.FC<QuestionCardProps> = ({
  question,
  questionTypes,
  readOnly,
  isFirst,
  isLast,
  onEdit,
  onDelete,
  onMoveUp,
  onMoveDown,
  onAddOption,
  onEditOption,
  onDeleteOption,
}) => {
  const type = questionTypes.find(t => t.id === question.typeId);
  const hasOptions = type ? OPTIONS_COMPONENTS.includes(type.htmlComponent as any) : false;

  return (
    <Card variant="outlined" sx={{ mb: 1.5 }}>
      <CardContent sx={{ py: 1.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
          <Box>
            <Typography variant="body1" fontWeight={500}>
              {question.nestCaptionI18n?.uk || '(без тексту)'}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} flexWrap="wrap">
              <Chip size="small" label={type?.comment || type?.name || `Тип #${question.typeId}`} />
              <Chip size="small" variant="outlined" label={question.name} />
              {!!question.isRequired && <Chip size="small" color="warning" label="Обов'язкове" />}
            </Stack>
          </Box>
          {!readOnly && (
            <Stack direction="row" spacing={0.5}>
              <Tooltip title="Підняти вище">
                <span>
                  <IconButton size="small" onClick={onMoveUp} disabled={isFirst}><ArrowUpward fontSize="small" /></IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Опустити нижче">
                <span>
                  <IconButton size="small" onClick={onMoveDown} disabled={isLast}><ArrowDownward fontSize="small" /></IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Редагувати питання">
                <IconButton size="small" onClick={onEdit}><Edit fontSize="small" /></IconButton>
              </Tooltip>
              <Tooltip title="Видалити питання">
                <IconButton size="small" onClick={onDelete}><Delete fontSize="small" /></IconButton>
              </Tooltip>
            </Stack>
          )}
        </Box>

        {hasOptions && (
          <Box sx={{ mt: 1.5, pl: 1, borderLeft: '2px solid', borderColor: 'divider' }}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {question.options.map(option => (
                <Chip
                  key={option.id}
                  label={option.nestCaptionI18n?.uk || option.caption || option.value}
                  size="small"
                  onClick={readOnly ? undefined : () => onEditOption(option.id)}
                  onDelete={readOnly ? undefined : () => onDeleteOption(option.id)}
                  variant={option.isDefault ? 'filled' : 'outlined'}
                />
              ))}
              {!readOnly && (
                <Chip
                  icon={<Add fontSize="small" />}
                  label="Варіант"
                  size="small"
                  variant="outlined"
                  onClick={onAddOption}
                />
              )}
            </Stack>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
